import ky from "ky";
import Cookies from "js-cookie";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { RecentlyViewedResponse } from "~/api/recently-viewed";

import { useSite } from "~/hooks/useSite";

/**
 * Return recently viewed handles without making an API request.
 */
export function useRecentlyViewedHandles() {
  return Cookies.get("last_viewed_products")?.split(",") ?? [];
}

/**
 * Return recently viewed products from /api/recently-viewed.
 */
export function useRecentlyViewed() {
  const handles = useRecentlyViewedHandles();

  const site = useSite();

  const { data, isPending } = useQuery({
    queryKey: ["recently-viewed"],
    queryFn: () =>
      ky
        .get("/api/recently-viewed", {
          headers: { "X-Site-Handle": site.handle },
        })
        .json<RecentlyViewedResponse>(),
  });

  return { products: data?.products ?? [], isPending, handles };
}

/**
 * Add the given product handle to recently viewed cookie.
 */
export function useUpdateRecentlyViewed() {
  const queryClient = useQueryClient();

  return ({ productHandle }: { productHandle: string }) => {
    // Get cookie (comma-delimited list of product handles).
    const cookie = Cookies.get("last_viewed_products");

    // Split cookie into an array.
    let handles = cookie ? cookie.split(",") : [];

    // Filter out the current handle so it does not appear twice.
    handles = handles.filter((handle) => handle !== productHandle);

    // Add current handle to start of array.
    handles.unshift(productHandle);

    // Limit length of handles to 10 and unique.
    handles = [...new Set(handles.slice(0, 10))];

    // Set cookie.
    Cookies.set("last_viewed_products", handles.join(","), {
      expires: 10, // 10 days
    });

    // Clear cached data for the useRecentlyViewed hook.
    queryClient.resetQueries({ queryKey: ["recently-viewed"] });
  };
}
